import { IContentCalloutTemplate } from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import styled from '@emotion/styled';
import { MenuItem, Select } from '@material-ui/core';
import { SetStateAction, useMemo } from 'react';
import { InfoBox } from '../../../../../../../../components/InfoBox';
import {
  MarkdownEditorWithReplacementTokens,
  TokenDefinition
} from '../../../../../../../../components/MarkdownText';
import { TextAlignToggleButtonGroupWithLabel } from '../../../../../../../../components/TextAlignToggleButtonGroup';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import { useIntegrationsContext } from '../../../../../../../../services/integrations/context';
import { TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_CALLOUT
} from '../../../validation';

type CalloutColor = IContentCalloutTemplate['data']['color'];

const SmallCalloutSquare = styled('div')<{
  color: CalloutColor;
}>((p) => ({
  background: p.theme.custom.colors[p.color].w200,
  borderLeft: `3px solid ${p.theme.custom.colors[p.color].w500}`,
  width: p.theme.spacing(2),
  height: p.theme.spacing(2)
}));

export const BlockSettingsContentCallout = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  tokens
}: {
  value: IContentCalloutTemplate;
  onChange: (nextValue: IContentCalloutTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { active } = useIntegrationsContext();
  const validateText = useFieldValidator(
    setValidation,
    VALIDATORS_CALLOUT,
    'text'
  );
  const invalidTextReason = validation?.fields.text || null;
  const colorOptions = useMemo<
    {
      label: string;
      color: CalloutColor;
    }[]
  >(() => {
    return [
      {
        label: t('Blue'),
        color: 'blue'
      },
      {
        label: t('Green'),
        color: 'green'
      },
      {
        label: t('Gray'),
        color: 'gray'
      },
      {
        label: t('Red'),
        color: 'red'
      },
      {
        label: t('Yellow'),
        color: 'yellow'
      },
      {
        label: t('Purple'),
        color: 'purple'
      }
    ];
  }, [t]);

  console.log('C', value.data.color);
  return (
    <FlexContainer
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      spacing={2}
    >
      <Select
        fullWidth
        value={value.data.color}
        onChange={(ev) =>
          onChange({
            ...value,
            data: {
              ...value.data,
              color: ev.target.value as CalloutColor
            }
          })
        }
      >
        {colorOptions.map((o) => (
          <MenuItem key={o.color} value={o.color}>
            <FlexContainer>
              <SmallCalloutSquare color={o.color} />
              <div>{o.label}</div>
            </FlexContainer>
          </MenuItem>
        ))}
      </Select>
      <MarkdownEditorWithReplacementTokens
        fullWidth
        value={value.data.t.t}
        autoFocus
        // This editor seems to store the onChange - if you change
        // the color it will jump back to the previous color.
        // But we can trick it by re-creating the component through a key
        // whenever that happens. As people cannot manipulate both
        // fields at the same time, the user won't notice a difference.
        key={`${value.data.color}-${value.data.t.textAlign || 'left'}`}
        onChange={(v) => {
          const nextValue: IContentCalloutTemplate = Object.assign({}, value, {
            data: Object.assign({}, value.data, {
              color: value.data.color,
              t: Object.assign({}, value.data.t, {
                t: v
              })
            })
          });

          invalidTextReason && validateText(nextValue, expTpl);
          onChange(nextValue);
        }}
        tokens={tokens}
        error={!!invalidTextReason}
        helperText={invalidTextReason}
        onBlur={() => validateText(value, expTpl)}
        rows={12}
        textAlign={value.data.t.textAlign || 'left'}
      />

      <TextAlignToggleButtonGroupWithLabel
        fullWidth
        value={value.data.t.textAlign || 'left'}
        onChange={(nextAlign) => {
          const nextValue: IContentCalloutTemplate = Object.assign({}, value, {
            data: Object.assign({}, value.data, {
              color: value.data.color,
              t: Object.assign({}, value.data.t, {
                textAlign: nextAlign
              })
            })
          });
          onChange(nextValue);
        }}
      />

      {active.has(Integration.SLACK) && (
        <InfoBox color="gray">
          {t(
            'Colored call-outs are not supported in Slack notifications. All call-outs will be shown as block-quotes.'
          )}
        </InfoBox>
      )}

      {active.has(Integration.MICROSOFT) && (
        <InfoBox color="gray">
          {t(
            'Call-outs in Teams notifications will be rendered in the theme colors set in Teams.'
          )}
        </InfoBox>
      )}
    </FlexContainer>
  );
};

export const BlockSettingsContentCalloutReadOnly = ({
  value
}: {
  value: IContentCalloutTemplate;
}) => null;
